import { formatMinor, type TaxChainStep } from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { explainTaxDecision, taxDecisionsQuery, taxKeys } from '../../shared/api/tax-queries.js';

/**
 * Why this record is taxed the way it is.
 *
 * The chain is always shown; the plain-English paragraph is optional and
 * arrives only if somebody asks for it. That ordering is the architecture made
 * visible: the reasoning is the rule, and the sentence is a convenience laid
 * over the top of it. If the sentence never generates, nothing is lost but
 * the sentence.
 */

export interface TaxTreatmentPanelProps {
  readonly organizationId: string;
  readonly recordId: string;
}

export function TaxTreatmentPanel({ organizationId, recordId }: TaxTreatmentPanelProps) {
  const decisions = useQuery(taxDecisionsQuery(organizationId, recordId));
  const queryClient = useQueryClient();

  const explain = useMutation({
    mutationFn: (decisionId: string) => explainTaxDecision(organizationId, decisionId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: taxKeys.decisions(organizationId, recordId) });
    },
  });

  if (decisions.isPending) {
    return (
      <Card>
        <CardHeader title="Tax treatment" />
        <CardBody>
          <p className="text-sm text-content-muted">Loading…</p>
        </CardBody>
      </Card>
    );
  }

  const all = decisions.data ?? [];
  // Newest first from the API; the current treatment is the first one, and the
  // rest are the history of how it got there.
  const current = all[0];
  const history = all.slice(1);

  if (current === undefined) {
    return (
      <Card>
        <CardHeader title="Tax treatment" />
        <CardBody>
          <p className="text-sm text-content-muted">
            No tax decision has been recorded for this record yet.
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Tax treatment"
        description="Decided by rule, from dated legislation. A model may put it into plain English; it never decides it."
      />
      <CardBody className="flex flex-col gap-4">
        {current.warnings.map((warning) => (
          <Alert key={warning} tone="warning">
            {warning}
          </Alert>
        ))}

        <div className="grid gap-3 sm:grid-cols-3 text-sm">
          <Fact label="Treated as" value={current.expenseClassification.replaceAll('_', ' ')} />
          <Fact label="Recoverable" value={`${current.itcEligiblePercent}%`} />
          <Fact label="Credit" value={formatMinor(current.itcAmountMinor)} />
        </div>

        <div>
          <p className="text-sm font-medium text-content">How it was decided</p>
          <ol className="mt-1 flex flex-col gap-2">
            {current.chain.map((step, index) => (
              <ChainStep key={`${step.step}-${index}`} step={step} />
            ))}
          </ol>
          <p className="mt-2 text-xs text-content-subtle">
            {current.tier}
            {current.pack === null ? '' : ` · ${current.pack}`}
            {current.fromVerifiedPack ? '' : ' · not yet checked against the legislation'}
          </p>
        </div>

        <div className="rounded-[var(--radius-surface)] border border-border bg-surface-sunken p-3">
          {current.explanation === null ? (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-content-muted">
                Want this in plain English? The decision above does not change either way.
              </p>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => explain.mutate(current.id)}
                isLoading={explain.isPending}
              >
                Explain
              </Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-content">{current.explanation}</p>
              <p className="mt-1 text-xs text-content-subtle">
                Written by {current.explanationModel ?? 'a model'} from the rule above. If this
                paragraph and the rule ever disagree, the rule is right.
              </p>
            </>
          )}
          {explain.isError ? (
            <p className="mt-2 text-xs text-danger">
              {explain.error instanceof Error
                ? explain.error.message
                : 'That explanation could not be generated.'}
            </p>
          ) : null}
        </div>

        {history.length === 0 ? null : (
          <details>
            <summary className="cursor-pointer text-sm text-content-muted">
              {history.length} earlier decision{history.length === 1 ? '' : 's'} on this record
            </summary>
            <ul className="mt-2 flex flex-col gap-1 text-xs text-content-muted">
              {history.map((decision) => (
                <li key={decision.id}>
                  {decision.decidedOn} · {decision.expenseClassification.replaceAll('_', ' ')} ·{' '}
                  {decision.itcEligiblePercent}% · {formatMinor(decision.itcAmountMinor)}
                  {decision.ruleName === null ? '' : ` · ${decision.ruleName}`}
                </li>
              ))}
            </ul>
          </details>
        )}
      </CardBody>
    </Card>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-content-muted">{label}</p>
      <p className="font-medium text-content">{value}</p>
    </div>
  );
}

function ChainStep({ step }: { step: TaxChainStep }) {
  return (
    <li className="border-l-2 border-border pl-3 text-sm">
      <p className="text-content">
        {step.ruleName ?? step.detail ?? step.step}
        {step.authority === null || step.authority === undefined ? null : (
          <span className="ml-1 rounded bg-brand-50 px-1.5 py-0.5 text-xs text-brand-700">
            {step.authority}
          </span>
        )}
      </p>
      {step.description === null || step.description === undefined ? null : (
        <p className="text-xs text-content-muted">{step.description}</p>
      )}
      {step.reason === null || step.reason === undefined ? null : (
        <p className="text-xs text-content-muted">Reason given: {step.reason}</p>
      )}
    </li>
  );
}
